import { Volume2, VolumeX, X } from "lucide-react";

/**
 * ChatHeader — Papaya avatar, name, status line, lotus counter, TTS toggle, close button.
 * Extracted from PapayaChat in iter-37 to keep PapayaChat under 1000 lines.
 *
 * The "lotus counter" branch:
 *   - exempt → ∞ with brighter gold border (non-clickable)
 *   - free   → "{n} free" (click opens the paywall as an upsell)
 *   - paid   → "{credits}" (click opens the paywall to add more)
 *   - other  → not rendered
 */
const PAPAYA_AVATAR = "https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/kq5buvwj_3acdc1ca-655b-474d-9a20-97c4a3a5100a.png";

const ChatHeader = ({
  isRecording, isTranscribing, cycleNum,
  accessReason, credits, freeInteractionsLeft, openPaywall,
  ttsEnabled, setTtsEnabled, stopTTS,
  onClose,
}) => {
  // Single function instead of an IIFE inside JSX so React DevTools can step into it
  const statusLine = () => {
    if (isRecording) return "Listening...";
    if (isTranscribing) return "Transcribing...";
    if (cycleNum > 0 && cycleNum < 5) return `Exchange ${cycleNum} of 5`;
    return "The White Rabbit";
  };
  const showCounter = accessReason === "paid" || accessReason === "free" || accessReason === "exempt";
  const isExempt = accessReason === "exempt";

  return (
    <div
      className="flex items-center justify-between p-4"
      style={{ background: "linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)" }}
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-white/30">
          <img src={PAPAYA_AVATAR} alt="Papaya" className="w-full h-full object-cover" />
        </div>
        <div>
          <h3 className="font-bold text-white text-sm" style={{ fontFamily: "'Fredoka', sans-serif" }}>Papaya</h3>
          <p className="text-xs text-white/70" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {statusLine()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {showCounter && (
          <button
            type="button"
            onClick={isExempt ? undefined : openPaywall}
            title={isExempt ? "Exempt account · unlimited lotuses" : "Your lotus balance · click to add more"}
            className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold"
            style={{
              background: isExempt ? "rgba(212,165,116,0.30)" : "rgba(212,165,116,0.18)",
              color: "#FFF8E7",
              border: isExempt ? "1px solid rgba(255,224,138,0.6)" : "1px solid rgba(212,165,116,0.4)",
              cursor: isExempt ? "default" : "pointer",
            }}
            data-testid="chat-lotus-counter"
          >
            <span style={{ filter: "drop-shadow(0 0 4px rgba(212,165,116,0.8))" }}>{"\u{1FAB7}"}</span>
            {isExempt ? (
              <span style={{ fontSize: "14px", lineHeight: 1 }} aria-label="unlimited">&infin;</span>
            ) : accessReason === "free" ? (
              <span>{freeInteractionsLeft}<span className="opacity-70 ml-0.5">free</span></span>
            ) : (
              <span>{credits}</span>
            )}
          </button>
        )}
        <button
          onClick={() => { setTtsEnabled(!ttsEnabled); stopTTS(); }}
          className="text-white/70 hover:text-white transition-colors"
          data-testid="toggle-tts"
        >
          {ttsEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors"
          data-testid="close-chat"
        >
          <X size={22} />
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
